import { createEntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../core/models/base-state/base-state';
import { loadProfileAndPrintPage } from '../../../core/store/actions/app.actions';
import { PatientImmunizationGroup } from '../../models/patient-immunization-group';
import {
  getPatientImmunizations,
  getPatientImmunizationsFailure,
  getPatientImmunizationsSuccess,
  toggleShowAll,
} from '../actions/immunization.actions';

export const immunizationFeatureKey = 'immunization';

export interface ImmunizationState extends EntityState<PatientImmunizationGroup> {
  baseState: baseState.BaseState;
}

export const immunizationAdapter = createEntityAdapter<PatientImmunizationGroup>({
  selectId: immunization => immunization.cvxGroupName,
});

export const initialState: ImmunizationState = immunizationAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
});

export const immunizationReducer = createReducer<ImmunizationState>(
  initialState,

  on(getPatientImmunizations, (state): ImmunizationState => {
    return {
      ...immunizationAdapter.removeAll(state),
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
    };
  }),

  on(getPatientImmunizationsSuccess, (state, { patientImmunizations }): ImmunizationState => {
    if (patientImmunizations !== null) return immunizationAdapter.addMany(patientImmunizations, state);
    return state;
  }),

  on(getPatientImmunizationsSuccess, (state): ImmunizationState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
    };
  }),

  on(getPatientImmunizationsFailure, (state, { error }): ImmunizationState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
    };
  }),

  on(toggleShowAll, (state, { cvxGroupName }): ImmunizationState => {
    const existingGroup = state.entities[cvxGroupName];
    if (!existingGroup) {
      return state;
    }
    const previousValue = existingGroup.showAll;

    const updatedGroup: Update<PatientImmunizationGroup> = {
      id: cvxGroupName,
      changes: {
        showAll: !previousValue,
      },
    };

    return immunizationAdapter.updateOne(updatedGroup, state);
  }),

  on(loadProfileAndPrintPage, (state): ImmunizationState => {
    const updates: Update<PatientImmunizationGroup>[] = Object.values(state.entities)
      .filter((g): g is PatientImmunizationGroup => !!g)
      .map(group => ({
        id: group.cvxGroupName, // already typed correctly
        changes: { showAll: true },
      }));

    return immunizationAdapter.updateMany(updates, state);
  })
);
